<template>
  <div class="generic-login">
    <header class="generic-login__header">
      <div class="brand-strip">
        <div class="brand-strip__segment brand-strip__segment--boticario"></div>
        <div class="brand-strip__segment brand-strip__segment--qdb"></div>
        <div class="brand-strip__segment brand-strip__segment--oui"></div>
        <div class="brand-strip__segment brand-strip__segment--eudora"></div>
      </div>
      <div class="generic-login__header-inner">
        <img src="/img/logo.png" alt="Grupo Boticário" class="generic-login__logo" />
      </div>
    </header>

    <main class="generic-login__content">
      <h1 class="generic-login__title">Olá!</h1>
      <p class="generic-login__subtitle">Preencha os campos abaixo para visualizar suas ofertas</p>

      <section class="generic-login__card">
        <form id="form" @submit.prevent="dologin">
          <label class="generic-login__field-label" for="accessdoc">Informe seu CPF</label>
          <input
            id="accessdoc"
            v-model="form.accessdoc"
            type="text"
            aria-label="cpf"
            class="generic-login__input"
            placeholder="000.000.000-00"
            :disabled="loading"
            v-mask="['###.###.###-##']"
          />

          <label class="generic-login__field-label" for="birthdate">Informe sua data de nascimento</label>
          <input
            id="birthdate"
            v-model="form.birthdate"
            type="text"
            aria-label="birthdate"
            class="generic-login__input"
            placeholder="dd/mm/aaaa"
            :disabled="loading"
            v-mask="['##/##/####']"
          />

          <div v-if="promoCodeErrors.length || customErrors.length" class="generic-login__errors">
            <b
              v-for="error in [...promoCodeErrors, ...customErrors]"
              :key="error"
              :class="classMessage"
              class="form-text"
            >
              {{ error }}
            </b>
          </div>

          <button
            v-if="!loading"
            :disabled="loading || form.accessdoc.length < 4 || form.birthdate.length < 10"
            type="submit"
            class="generic-login__button"
          >
            CONTINUAR
          </button>
        </form>
        <ProgressLoading :loading="loading" />
      </section>
    </main>

    <footer class="generic-login__footer">
      <div class="generic-login__footer-logos">
        <img src="/img/img-footer-mobile.png" alt="Revendedores Grupo Boticário e PGMais" class="generic-login__footer-logo" />
      </div>
      <p class="generic-login__footer-copy">Copyright © 2026.</p>
      <p class="generic-login__footer-copy generic-login__footer-copy--small">
        Todos direitos reservados a PGMais.
      </p>
    </footer>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { mask } from 'vue-the-mask';
import { validationMixin } from 'vuelidate';
import { required, minLength } from 'vuelidate/lib/validators';
import ProgressLoading from '../../modules/auth/components/ProgressLoading.vue';
import { validaCpfCnpj, ActionGetIPAddress } from '@/utils/helpers';
import { markCampaignPopupPending } from '@/pages/home/utils/campaignPopup';

export default {
  components: {
    ProgressLoading,
  },
  name: 'GenericLogin',
  directives: { mask },
  mixins: [validationMixin],
  validations: {
    form: {
      accessdoc: { required, minLength: minLength(4) },
      birthdate: { required, minLength: minLength(10) },
    },
  },
  data: () => ({
    themeBackground: null,
    imageBackground: null,
    form: {
      accessdoc: '',
      birthdate: '',
      termsAcepted: false,
    },
    error: {
      alert: false,
      status: '',
      message: null,
    },
    loading: false,
    ipUser: null,
    customErrors: [],
  }),
  methods: {
    ...mapActions('auth', ['ActionGenericDoLogin']),
    async dologin() {
      this.customErrors = [];
      this.error.status = 'ERROR';
      this.$v.$touch();

      const isDocValid = validaCpfCnpj(this.form.accessdoc);

      if (this.$v.$invalid || !isDocValid) {
        this.loading = false;
        if (!isDocValid) {
          this.error.status = 'ERROR';
          this.customErrors.push('Informe um CPF/CNPJ válido.');
          this.loading = false;
        }
      } else {
        this.loading = true;
        const accessdoc = this.form.accessdoc.replace(/\D+/g, '');
        try {
          const birthdateFormatted = this.formatBirthdate(this.form.birthdate);

          await this.ActionGenericDoLogin({
            DocNumber: accessdoc,
            BirthDate: birthdateFormatted,
            DeviceName: process.env.VUE_APP_API_DEVICE,
            IP: this.ipUser || '',
            OS: navigator.platform,
          });
          this.loading = false;
          // Popup de campanha: 1x ao cair no dashboard.
          markCampaignPopupPending();
          this.$router.replace({ name: 'home' }).catch(() => {});
        } catch {
          this.customErrors.push(
            'Ocorreu um erro ao autenticar. Dados informados inválidos.'
          );
          this.error.status = 'ERROR';
          this.loading = false;
        }
        this.loading = false;
      }
    },

    formatBirthdate(dateString) {
      if (!dateString) return null;
      const parts = dateString.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return dateString;
    },

    async getIPAddressUser() {
      let maxAttempts = 10;
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          this.ipUser = await ActionGetIPAddress();
          break;
        } catch {
          attempts++;
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    },
  },
  async created() {
    await this.getIPAddressUser();
  },
  computed: {
    promoCodeErrors() {
      const errors = [];

      if (!this.$v.form.accessdoc.$dirty && !this.$v.form.birthdate.$dirty) return errors;
      !this.$v.form.accessdoc.required && errors.push('Campo CPF/CNPJ é obrigatório!');
      !this.$v.form.accessdoc.minLength &&
        errors.push('O CPF/CNPJ não está completo!');
      !this.$v.form.birthdate.required && errors.push('Data de nascimento é obrigatória!');
      !this.$v.form.birthdate.minLength &&
        errors.push('Data de nascimento deve estar no formato DD/MM/AAAA!');

      return errors;
    },
    classMessage() {
      return {
        'text-danger': this.error.status === 'ERROR',
        'text-muted': this.error.status !== 'ERROR',
      };
    },
  },
};
</script>
<style scoped>
.generic-login {
  min-height: 100dvh;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.generic-login__header {
  background: #fff;
  border-bottom: 1px solid #dfdfdf;
}

.brand-strip {
  display: flex;
  width: 100%;
  height: 10px;
}

.brand-strip__segment {
  flex: 1;
}

.brand-strip__segment--boticario {
  background: #2da072;
}

.brand-strip__segment--qdb {
  background: #d245ad;
}

.brand-strip__segment--oui {
  background: #8d2231;
}

.brand-strip__segment--eudora {
  background: #613a53;
}

.generic-login__header-inner {
  height: 82px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.generic-login__logo {
  width: 146px;
  height: auto;
}

.generic-login__content {
  flex: 1;
  width: 100%;
  max-width: 328px;
  margin: 0 auto;
  padding: 24px 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.generic-login__title {
  margin: 0;
  text-align: center;
  color: #333438;
  font-family: 'Inter', sans-serif;
  font-size: 42px;
  line-height: 1.08;
  font-weight: 700;
}

.generic-login__subtitle {
  margin: 10px 20px 24px;
  text-align: center;
  color: #333438;
  font-family: 'Inter', sans-serif;
  font-size: 19px;
  line-height: 1.2;
  font-weight: 500;
}

.generic-login__card {
  background: #efefef;
  border-radius: 18px;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.06),
    0 8px 18px -6px rgba(0, 0, 0, 0.2);
  padding: 20px 16px 14px;
}

.generic-login__field-label {
  display: block;
  margin: 0 0 8px;
  color: #555;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.3;
}

.generic-login__input {
  width: 100%;
  height: 44px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  color: #50545a;
  padding: 0 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  margin-bottom: 14px;
}

.generic-login__input::placeholder {
  color: #9b9b9b;
}

.generic-login__input:focus {
  outline: none;
  border-color: #88b2a6;
  box-shadow: 0 0 0 2px rgba(112, 150, 127, 0.15);
}

.generic-login__errors {
  text-align: left;
  font-size: 11px;
  margin-bottom: 10px;
}

.generic-login__button {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 7px;
  background: #06a472;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.generic-login__button:hover:not(:disabled) {
  background: #05966a;
}

.generic-login__button:disabled {
  cursor: not-allowed;
  background: #b8d4ca;
  color: rgba(255, 255, 255, 0.92);
  opacity: 1;
}

.generic-login__footer {
  padding: 12px 16px 18px;
  text-align: center;
  color: #8e8e8e;
  background: #efefef;
}

.generic-login__footer-logos {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.generic-login__footer-logo {
  width: 220px;
  height: auto;
}

.generic-login__footer-copy {
  margin: 0;
  font-size: 12px;
  line-height: 1.25;
  text-align: left;
}

.generic-login__footer-copy--small {
  font-size: 11px;
}

@media (max-width: 420px) {
  .generic-login__title {
    font-size: 40px;
  }

  .generic-login__subtitle {
    font-size: 16px;
  }
}
</style>

